import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, Mic, RefreshCw, Sparkles, Square, Trash2, Upload, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label, NativeSelect, Textarea } from '@/components/ui/input';
import { Spinner } from '@/components/ui/states';
import { api, ApiError } from '@/lib/api';
import { scaleImageFile } from '@/features/products/images';

interface MyModel {
  id: number;
  assetIds: number[];
  workAssetIds: number[];
  description: string;
  portraitAssetId: number | null;
  hasVoice: boolean;
  voiceMs: number | null;
  voicePreset: string | null;
}
interface MyMedia {
  model: MyModel | null;
  voiceConsentAt: string | null;
  voices: { id: string; name: string; gender: string; note: string; enabled: boolean }[];
}

const SLOTS = ['Gương mặt', 'Nghiêng 3/4', 'Toàn thân'] as const;
/** Anh Tiến 26/10: "thêm ảnh làm việc 2-3 ảnh" (ở kho, công trình, văn phòng) → AI lấy dáng làm việc, bối cảnh thật khi vẽ cảnh phim */
const WORK_SLOTS = ['Làm việc 1', 'Làm việc 2', 'Làm việc 3'] as const;
const ALL_SLOTS = [...SLOTS, ...WORK_SLOTS];
const SAMPLE = (name: string) =>
  `Xin chào anh chị, em là ${name}, nhân viên Công ty Minh Tiến. Bên em chuyên thép ống, inox, thiết bị phòng cháy chữa cháy và quạt hút công nghiệp. Hàng có sẵn kho, giao nhanh trong ngày. Anh chị cần báo giá cứ gọi em nhé!`;
const errMsg = (e: unknown, fb: string) => (e instanceof ApiError || e instanceof Error ? e.message : fb);

function useBlobUrl(path: string | null) {
  const q = useQuery({ queryKey: ['blob', path], queryFn: () => api.blob(path!), enabled: !!path, staleTime: 5 * 60_000 });
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!q.data || !path) return setUrl(null);
    const u = URL.createObjectURL(q.data);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [q.data, path]);
  return url;
}

function Thumb({ id, alt, className }: { id: number; alt: string; className: string }) {
  const src = useBlobUrl(`/me/media/assets/${id}/file`);
  return src ? <img src={src} alt={alt} className={className} /> : <div className={`${className} animate-pulse bg-muted`} />;
}

/** Định dạng file ghi âm theo đuôi khi trình duyệt không báo (iPhone hay để trống với file .m4a) */
const AUDIO_EXT: Record<string, string> = { m4a: 'audio/mp4', mp4: 'audio/mp4', aac: 'audio/aac', mp3: 'audio/mpeg', wav: 'audio/wav', webm: 'audio/webm', ogg: 'audio/ogg', oga: 'audio/ogg' };
const audioMime = (f: File) => (f.type && f.type.startsWith('audio/') ? f.type : (AUDIO_EXT[f.name.split('.').pop()?.toLowerCase() ?? ''] ?? f.type));
/** Độ dài file ghi âm (ms) đọc từ thẻ audio */
const audioMs = (url: string) =>
  new Promise<number>((resolve) => {
    const a = new Audio();
    a.preload = 'metadata';
    a.onloadedmetadata = () => resolve(Number.isFinite(a.duration) && a.duration > 0 ? a.duration * 1000 : 0);
    a.onerror = () => resolve(0);
    a.src = url;
  });

async function blobToBase64(b: Blob) {
  const buf = new Uint8Array(await b.arrayBuffer());
  let bin = '';
  for (let i = 0; i < buf.length; i += 0x8000) bin += String.fromCharCode(...buf.subarray(i, i + 0x8000));
  return btoa(bin);
}

/**
 * DEC-517 (anh Tiến 26/10/2026): "Đưa luôn vào hồ sơ nhân viên: cung cấp hình ảnh và lời nói ở mục chữ ký".
 * NV nộp 1–3 ảnh thật + ghi âm giọng đọc đoạn mẫu + chọn giọng máy gần nhất, kèm đồng ý → ERP tạo Mẫu nhân viên
 * và AI làm 1 ảnh chuẩn toàn thân mặc đồng phục để vào vai trong phim / ảnh quảng cáo.
 */
export function MyMediaField({ fullName }: { fullName: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['me', 'media'], queryFn: () => api.get<MyMedia>('/me/media') });
  const m = q.data?.model ?? null;
  /** 0–2: ảnh hồ sơ, 3–5: ảnh đang làm việc */
  const [photos, setPhotos] = useState<(number | null)[]>([null, null, null, null, null, null]);
  const [desc, setDesc] = useState('');
  const [preset, setPreset] = useState('');
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState<'' | 'photo' | 'save' | 'portrait' | 'voice'>('');
  const slotInput = useRef<HTMLInputElement>(null);
  const slotIdx = useRef(0);
  useEffect(() => {
    if (!q.data) return;
    const ids = q.data.model?.assetIds ?? [];
    const work = q.data.model?.workAssetIds ?? [];
    setPhotos([0, 1, 2].map((k) => ids[k] ?? null).concat([0, 1, 2].map((k) => work[k] ?? null)));
    setDesc(q.data.model?.description ?? '');
    setPreset(q.data.model?.voicePreset ?? '');
    setConsent(!!q.data.model);
  }, [q.data]);
  const refresh = () => qc.invalidateQueries({ queryKey: ['me', 'media'] });

  // Ghi âm giọng
  const [rec, setRec] = useState<MediaRecorder | null>(null);
  const [take, setTake] = useState<{ blob: Blob; url: string; ms: number; mime: string } | null>(null);
  const voiceFile = useRef<HTMLInputElement>(null);
  const [voiceConsent, setVoiceConsent] = useState(false);
  const started = useRef(0);
  const savedVoice = useBlobUrl(m?.hasVoice ? `/sales-studio/models/${m.id}/voice?v=${m.voiceMs}` : null);
  useEffect(
    () => () => {
      if (take) URL.revokeObjectURL(take.url);
    },
    [take],
  );

  const pickPhoto = (i: number) => {
    slotIdx.current = i;
    // Ảnh hồ sơ mở camera trước, ảnh làm việc mở camera sau
    slotInput.current?.setAttribute('capture', i < 3 ? 'user' : 'environment');
    slotInput.current?.click();
  };
  const uploadPhoto = async (f: File) => {
    setBusy('photo');
    try {
      const s = await scaleImageFile(f);
      const a = await api.post<{ id: number }>('/me/media/photos', { ...s, title: `Ảnh hồ sơ ${fullName} – ${ALL_SLOTS[slotIdx.current]}` });
      setPhotos((p) => p.map((x, i) => (i === slotIdx.current ? a.id : x)));
    } catch (e) {
      toast.error(errMsg(e, 'Không tải được ảnh'));
    } finally {
      setBusy('');
    }
  };

  const makePortrait = async () => {
    setBusy('portrait');
    try {
      await api.post('/me/media/portrait');
      await refresh();
      toast.success('AI đã tạo mẫu chuẩn của bạn');
    } catch (e) {
      toast.warning(`Chưa tạo được mẫu chuẩn: ${errMsg(e, 'lỗi AI')}. Mẫu vẫn dùng ảnh thật, bấm "Tạo lại mẫu chuẩn" sau.`);
    } finally {
      setBusy('');
    }
  };
  const save = async () => {
    const assetIds = photos.slice(0, 3).filter((x): x is number => x != null);
    const workAssetIds = photos.slice(3).filter((x): x is number => x != null);
    setBusy('save');
    let saved: MyModel;
    try {
      saved = await api.put<MyModel>('/me/media', { assetIds, workAssetIds, description: desc, voicePreset: preset || null, consent });
      await refresh();
      toast.success('Đã lưu hình ảnh của bạn');
    } catch (e) {
      toast.error(errMsg(e, 'Không lưu được'));
      setBusy('');
      return;
    }
    setBusy('');
    if (!saved.portraitAssetId) await makePortrait();
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      const r = new MediaRecorder(stream);
      const chunks: Blob[] = [];
      r.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      r.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks, { type: r.mimeType || 'audio/webm' });
        setTake({ blob, url: URL.createObjectURL(blob), ms: Date.now() - started.current, mime: blob.type });
        setRec(null);
      };
      started.current = Date.now();
      r.start();
      setRec(r);
    } catch {
      toast.error('Không mở được micro. Cho phép trình duyệt dùng micro rồi thử lại.');
    }
  };
  /** Tải file ghi âm có sẵn (app Ghi âm của iPhone .m4a, mp3, wav) thay cho ghi trực tiếp */
  const pickVoiceFile = async (f: File) => {
    if (f.size > 8 * 1024 * 1024) return toast.error('File quá lớn (tối đa 8 MB, khoảng 3 phút). Cắt ngắn rồi tải lại.');
    const url = URL.createObjectURL(f);
    const ms = await audioMs(url);
    if (!ms) {
      URL.revokeObjectURL(url);
      return toast.error('Không đọc được file này. Dùng file m4a, mp3 hoặc wav, hoặc bấm ghi âm trực tiếp.');
    }
    if (ms > 180_000) {
      URL.revokeObjectURL(url);
      return toast.error('File dài quá 3 phút. Chỉ cần đọc đoạn mẫu khoảng 15–30 giây.');
    }
    setTake({ blob: f, url, ms, mime: audioMime(f) });
  };
  const saveVoice = async () => {
    if (!take) return;
    setBusy('voice');
    try {
      await api.put('/me/media/voice', { data: await blobToBase64(take.blob), mime: take.mime || 'audio/webm', ms: Math.max(3000, Math.round(take.ms)), consent: voiceConsent });
      setTake(null);
      await refresh();
      toast.success('Đã lưu giọng của bạn');
    } catch (e) {
      toast.error(errMsg(e, 'Không lưu được giọng'));
    } finally {
      setBusy('');
    }
  };
  const run = async (fn: () => Promise<unknown>, msg: string) => {
    try {
      await fn();
      await refresh();
      toast.success(msg);
    } catch (e) {
      toast.error(errMsg(e, 'Không thực hiện được'));
    }
  };

  if (!q.data) return null;
  const hasPhoto = photos.slice(0, 3).some((x) => x != null);
  const slot = (label: string, i: number) => (
    <div key={label} className="flex flex-col items-center gap-1">
      <button
        type="button"
        onClick={() => pickPhoto(i)}
        disabled={!!busy}
        className="relative flex h-24 w-20 items-center justify-center overflow-hidden rounded-md border border-dashed bg-muted/30 hover:border-primary"
        data-testid={`my-media-slot-${i}`}
      >
        {photos[i] ? <Thumb id={photos[i]} alt={label} className="h-full w-full object-cover" /> : <Camera className="size-5 text-muted-foreground" />}
        {busy === 'photo' && slotIdx.current === i && <Spinner className="absolute" />}
      </button>
      <span className="text-xs text-muted-foreground">{label}</span>
      {photos[i] && (
        <button
          type="button"
          className="text-xs text-muted-foreground hover:text-destructive"
          onClick={() => setPhotos((p) => p.map((x, k) => (k === i ? null : x)))}
        >
          <X className="inline size-3" /> Bỏ
        </button>
      )}
    </div>
  );
  return (
    <div className="space-y-3 rounded-md border p-3" data-testid="my-media">
      <div>
        <Label className="text-sm">Hình ảnh &amp; giọng nói (vào vai trong phim, ảnh quảng cáo)</Label>
        <p className="text-xs text-muted-foreground">
          Nộp ảnh thật và giọng đọc của bạn. ERP tự tạo 1 mẫu chuẩn mặc đồng phục Minh Tiến để bạn vào vai trong video bán hàng.
        </p>
      </div>

      <input
        ref={slotInput}
        type="file"
        accept="image/*"
        capture="user"
        className="hidden"
        data-testid="my-media-file"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) void uploadPhoto(f);
        }}
      />
      <div className="flex flex-wrap items-end gap-3">
        {SLOTS.map((label, i) => slot(label, i))}
        <div className="flex flex-col items-center gap-1">
          <div className="flex h-24 w-20 items-center justify-center overflow-hidden rounded-md border bg-accent" data-testid="my-media-portrait">
            {busy === 'portrait' ? (
              <Spinner />
            ) : m?.portraitAssetId ? (
              <Thumb id={m.portraitAssetId} alt="Mẫu chuẩn" className="h-full w-full object-cover" />
            ) : (
              <Sparkles className="size-5 text-primary" />
            )}
          </div>
          <span className="text-xs font-medium text-primary">Mẫu chuẩn AI</span>
          {m && !busy && (
            <button type="button" className="text-xs text-muted-foreground hover:text-primary" onClick={() => void makePortrait()}>
              <RefreshCw className="inline size-3" /> Tạo lại
            </button>
          )}
        </div>
      </div>
      {busy === 'portrait' && <p className="text-xs text-muted-foreground">AI đang chụp lại mẫu chuẩn của bạn, khoảng 30–60 giây…</p>}

      <div>
        <Label>Ảnh đang làm việc (2–3 ảnh, không bắt buộc)</Label>
        <p className="mb-1 text-xs text-muted-foreground">
          Ảnh thật lúc bạn làm việc: ở kho, công trình, giao hàng, văn phòng. AI lấy dáng làm việc và bối cảnh thật khi vẽ cảnh phim có bạn.
        </p>
        <div className="flex flex-wrap items-end gap-3">{WORK_SLOTS.map((label, i) => slot(label, i + 3))}</div>
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        <div>
          <Label>Dáng người (không bắt buộc)</Label>
          <Textarea rows={2} maxLength={1000} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="VD cao 1m70, người gọn, tóc ngắn, đeo kính" />
        </div>
        <div>
          <Label>Giọng máy gần giọng bạn nhất</Label>
          <NativeSelect value={preset} onChange={(e) => setPreset(e.target.value)} data-testid="my-media-preset">
            <option value="">(Chọn giọng)</option>
            {q.data.voices
              .filter((v) => v.enabled || v.id === preset)
              .map((v) => (
                <option key={v.id} value={v.id} disabled={!v.enabled}>
                  {v.name} · {v.note}
                </option>
              ))}
          </NativeSelect>
          <p className="mt-1 text-xs text-muted-foreground">
            Dùng lồng câu thoại của bạn khi chưa tự ghi âm câu đó.
            {q.data.voices.some((v) => !v.enabled) &&
              ` Giọng ${q.data.voices
                .filter((v) => !v.enabled)
                .map((v) => v.name)
                .join(', ')} sẽ hiện khi công ty bật giọng đọc Việt.`}
          </p>
        </div>
      </div>
      <label className="flex items-start gap-2 text-sm">
        <input type="checkbox" className="mt-0.5 size-4" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="my-media-consent" />
        Tôi đồng ý cho Công ty Minh Tiến dùng hình ảnh của tôi trong ảnh và video quảng cáo. Tôi rút lại được bất cứ lúc nào.
      </label>
      <div className="flex flex-wrap gap-2">
        {(!hasPhoto || !consent) && (
          <p className="w-full text-xs text-warning-foreground" data-testid="my-media-save-hint">
            {!hasPhoto ? 'Chụp hoặc tải ít nhất 1 ảnh hồ sơ ở trên' : 'Tick ô đồng ý ở trên'} để bật nút Lưu. Lưu xong mới ghi âm / tải giọng được.
          </p>
        )}
        <Button type="button" size="sm" disabled={!!busy || !hasPhoto || !consent} onClick={() => void save()} data-testid="my-media-save">
          {busy === 'save' ? <Spinner /> : <Sparkles />} Lưu &amp; tạo mẫu chuẩn
        </Button>
        {m && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={!!busy}
            onClick={() =>
              confirm('Rút đồng ý? Mẫu của bạn sẽ bị gỡ và bản ghi giọng bị xóa.') && void run(() => api.del('/me/media'), 'Đã rút đồng ý, gỡ mẫu của bạn')
            }
          >
            <Trash2 /> Rút đồng ý
          </Button>
        )}
      </div>

      <div className="space-y-2 border-t pt-3">
        <Label>Giọng nói thật của bạn</Label>
        {!m ? (
          <p className="text-xs text-muted-foreground" data-testid="my-media-voice-locked">
            Bước 1: chụp ít nhất 1 ảnh hồ sơ, tick đồng ý, bấm <b>Lưu &amp; tạo mẫu chuẩn</b>. Bước 2: quay lại đây bấm ghi âm hoặc <b>Tải file ghi âm lên</b> (file
            m4a của app Ghi âm iPhone, mp3, wav).
          </p>
        ) : (
          <>
            <p className="rounded-md bg-muted/40 p-2 text-sm italic" data-testid="my-media-sample">
              “{SAMPLE(fullName)}”
            </p>
            <div className="flex flex-wrap items-center gap-2">
              {rec ? (
                <Button type="button" size="sm" variant="destructive" onClick={() => rec.stop()} data-testid="my-media-rec-stop">
                  <Square /> Dừng ghi
                </Button>
              ) : (
                <Button type="button" size="sm" variant="outline" disabled={!!busy} onClick={() => void startRec()} data-testid="my-media-rec">
                  <Mic /> {take || m.hasVoice ? 'Ghi lại' : 'Bấm và đọc đoạn trên'}
                </Button>
              )}
              {!rec && (
                <Button type="button" size="sm" variant="outline" disabled={!!busy} onClick={() => voiceFile.current?.click()} data-testid="my-media-voice-upload">
                  <Upload /> Tải file ghi âm lên
                </Button>
              )}
              <input
                ref={voiceFile}
                type="file"
                accept="audio/*,.m4a,.mp3,.wav,.aac,.ogg,.webm"
                className="hidden"
                data-testid="my-media-voice-file"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (f) void pickVoiceFile(f);
                }}
              />
              {rec && <span className="text-xs text-destructive">● Đang ghi… đọc to, rõ, chỗ yên tĩnh</span>}
              {take && <audio controls src={take.url} className="h-8" />}
              {!take && savedVoice && <audio controls src={savedVoice} className="h-8" data-testid="my-media-voice" />}
              {!take && m.hasVoice && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => confirm('Xóa giọng đã ghi?') && void run(() => api.del('/me/media/voice'), 'Đã xóa giọng')}
                >
                  <Trash2 /> Xóa giọng
                </Button>
              )}
            </div>
            {take && (
              <>
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5 size-4"
                    checked={voiceConsent}
                    onChange={(e) => setVoiceConsent(e.target.checked)}
                    data-testid="my-media-voice-consent"
                  />
                  Tôi đồng ý cho công ty dùng giọng nói của tôi trong video bán hàng.
                </label>
                <Button type="button" size="sm" disabled={!!busy || !voiceConsent} onClick={() => void saveVoice()} data-testid="my-media-voice-save">
                  {busy === 'voice' ? <Spinner /> : <Mic />} Lưu giọng ({Math.round(take.ms / 1000)} giây)
                </Button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
